// Cliente del chat con el servidor MCP (backend: asistente_chat, /api/asistente/).
//
// estado/ y conversaciones/ van por axios (interceptores: baseURL y refresh del JWT).
// mensaje/ es un SSE por POST: se lee con fetch + ReadableStream. El token va en el
// encabezado Authorization, nunca en la URL.
import axios from 'axios';
import '../../../_API/axiosConfig';
import { getStoredAuthHeaders } from '../../../_Utils/authHeaders';
import { downloadBlobResponse } from '../../Private/Reporte/reportesConvenioUtils';

export const getEstado = async () => (await axios.get('/api/asistente/estado/')).data;

// Términos del piloto: el titular acepta por la administración (nivel 'titular') y cada
// usuario reconoce el aviso (nivel 'usuario'). Siempre sobre la versión vigente.
export const aceptarTerminos = async (version, nivel) =>
  (await axios.post('/api/asistente/terminos/aceptar/', { version, nivel })).data;

export const getConversaciones = async (q = '') =>
  (await axios.get('/api/asistente/conversaciones/', { params: q ? { q } : {} })).data;

// Acciones: el modelo propone, la persona decide con estos botones (backend: asistente_chat.acciones).
export const getAcciones = async (estado = '') =>
  (await axios.get('/api/asistente/acciones/', { params: estado ? { estado } : {} })).data;
// seleccion: en las acciones donde la persona elige filas (p. ej. confirmar borradores), los ids elegidos.
// importes: en las que tienen un importe editable por fila (registrar cobranzas), los de la pantalla; el backend
// rechaza si no coinciden con los de la última propuesta preparada.
export const confirmarAccion = async (id, seleccion, importes) => {
  const cuerpo = {};
  if (seleccion) cuerpo.seleccion = seleccion;
  if (importes) cuerpo.importes = importes;
  return (await axios.post(`/api/asistente/acciones/${id}/confirmar/`, Object.keys(cuerpo).length ? cuerpo : undefined)).data;
};
// Vuelve a preparar la propuesta con los importes editados (avisos, ensayo y huella nuevos).
export const editarAccion = async (id, importes) =>
  (await axios.post(`/api/asistente/acciones/${id}/editar/`, { importes })).data;
export const cancelarAccion = async (id) => (await axios.post(`/api/asistente/acciones/${id}/cancelar/`)).data;
export const deshacerAccion = async (id) => (await axios.post(`/api/asistente/acciones/${id}/deshacer/`)).data;

// Conciliación asistida: el extracto se sube por un endpoint aparte (gunicorn; /api/asistente/ acepta hasta 1 MB) con el
// mismo lector que la pantalla. Devuelve {id, nombre, renglones, desde, hasta, debitos, creditos}; el chat le pasa al
// modelo el número de extracto, nunca el archivo.
export const subirExtracto = async (archivo, consorcioId) => {
  const datos = new FormData();
  datos.append('archivo', archivo);
  datos.append('consorcio_id', consorcioId);
  return (await axios.post('/api/conciliaciones/extractos-chat/', datos)).data;
};

const fechaCorta = (iso) => (iso ? iso.split('-').reverse().join('/') : '');
// Lo que va al chat para que el modelo sepa qué extracto proponer importar.
export const textoExtractoAdjunto = (d) =>
  `[Extracto adjunto N° ${d.id}: «${d.nombre}», ${d.renglones} renglones del ${fechaCorta(d.desde)} al ${fechaCorta(d.hasta)}]`;

export const getConversacion = async (id) =>
  (await axios.get(`/api/asistente/conversaciones/${id}/`)).data;

// Descarga con el JWT (axios lo agrega): un <a href> común no lleva el encabezado Authorization.
export const descargarArchivo = async (url, nombrePorDefecto) => {
  const respuesta = await axios.get(url, { responseType: 'blob' });
  downloadBlobResponse(respuesta, nombrePorDefecto);
};

// Separa un buffer SSE en eventos completos. Devuelve { eventos, resto }.
export const parseSSE = (buffer) => {
  const eventos = [];
  const bloques = buffer.split('\n\n');
  const resto = bloques.pop();
  bloques.forEach((bloque) => {
    let nombre = 'message';
    const datos = [];
    bloque.split('\n').forEach((linea) => {
      if (linea.startsWith(':')) return; // comentario (": ok")
      if (linea.startsWith('event:')) nombre = linea.slice(6).trim();
      else if (linea.startsWith('data:')) datos.push(linea.slice(5).replace(/^ /, ''));
    });
    if (!datos.length) return;
    try {
      eventos.push({ nombre, datos: JSON.parse(datos.join('\n')) });
    } catch {
      // un evento ilegible no corta el resto
    }
  });
  return { eventos, resto };
};

export class ErrorAsistente extends Error {
  constructor(mensaje, { codigo = 'error', status = 0 } = {}) {
    super(mensaje);
    this.codigo = codigo;
    this.status = status;
  }
}

// Manda la pregunta y llama a onEvento({nombre, datos}) por cada evento del stream.
export const enviarMensaje = async (cuerpo, { onEvento, signal } = {}) => {
  const base = String(axios.defaults.baseURL || '').replace(/\/+$/, '');
  const respuesta = await fetch(`${base}/api/asistente/mensaje/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'text/event-stream', ...(getStoredAuthHeaders() || {}) },
    body: JSON.stringify(cuerpo),
    signal,
  });
  if (!respuesta.ok) {
    let datos = {};
    try {
      datos = await respuesta.json();
    } catch {
      // sin cuerpo JSON
    }
    throw new ErrorAsistente(datos.mensaje || 'El asistente no pudo responder. Probá de nuevo.', {
      codigo: datos.codigo || `http_${respuesta.status}`,
      status: respuesta.status,
    });
  }
  const lector = respuesta.body.getReader();
  const decodificador = new TextDecoder();
  let buffer = '';
  let terminado = false;
  while (!terminado) {
    const { value, done } = await lector.read();
    terminado = done;
    if (done) break;
    buffer += decodificador.decode(value, { stream: true });
    const { eventos, resto } = parseSSE(buffer);
    buffer = resto;
    eventos.forEach((ev) => onEvento && onEvento(ev));
  }
  const { eventos } = parseSSE(`${buffer}\n\n`);
  eventos.forEach((ev) => onEvento && onEvento(ev));
};
